<template>
  <div>
    <ForumHeader
      :has-notice="hasNotice"
    />
    <div class="forum-homepage">
      <div class="forum-content">
        <div
          v-if="tags.length"
          class="tag-list"
        >
          <Tag
            v-for="tag in tags"
            :key="tag"
            class="post-tag"
            closable
            @on-close="removeTag(tag)"
          >
            {{ tag }}
          </Tag>
        </div>
        <div class="thread-list-wrapper">
          <Spin
            v-if="loading"
            fix
          >
            <i
              class="fas fa-spinner fa-spin"
              style="font-size: 40px; color: var(--color-green-thick);"
            />
            <p>Loading</p>
          </Spin>
          <template v-else>
            <List
              class="thread-list"
              :style="listCssStyle"
            >
              <PostThread
                v-for="thread in threadsForPage"
                :key="thread.id"
                :thread-data="thread"
                :page-view-switch="pageViewSwitch"
                class="thread-list-content"
                @click.native="openPostDetail(thread)"
              />
              <div
                slot="header"
                class="thread-list-header"
              >
                <div class="thread-heading">
                  Post
                </div>
                <div class="thread-reply">
                  Replies
                </div>
                <div class="thread-like">
                  Likes
                </div>
                <div class="thread-last-updated">
                  Last Reply
                </div>
                <Dropdown
                  :transfer="true"
                  placement="bottom-end"
                  class="thread-sort"
                  @on-click="onUpdateSort"
                >
                  <a href="javascript:void(0)">
                    Sort by
                    <Icon type="ios-arrow-down" />
                  </a>
                  <DropdownMenu slot="list">
                    <DropdownItem
                      v-for="rule in Object.keys(sortRules)"
                      :key="rule"
                      :selected="sortRuleIndex === sortRules[rule]"
                      :name="rule"
                    >
                      {{ rule }}
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
              <Page
                slot="footer"
                :current="currentPage"
                :total="filteredThreads.length"
                :page-size="pageSize"
                show-elevator
                @on-change="onPageChange"
              />
            </List>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import PostThread from './helper-components/forum/PostThread'
import ForumHeader from './helper-components/forum/ForumHeader'
import UserMixin from '@/mixins/UserMixin'

export default {
  components: {
    PostThread,
    ForumHeader
  },

  mixins: [
    UserMixin
  ],

  props: {
    hasNotice: Boolean,
    userId: Number
  },

  data () {
    return {
      loading: true,
      dummyThreadInfo: {
        id: 1,
        title: 'This is a title',
        author: this.$auth.getUser(),
        lastUpdated: this.$moment(new Date()).fromNow(),
        numReplies: 10,
        numThumbup: 5,
        tags: ['table', 'dashboard'],
        pageViewSwitch: false
      }
    }
  },

  computed: {
    tags () {
      return (this.$route.query && this.$route.query.tags && this.$route.query.tags.split(',')) || []
    },

    ...mapState('forum', {
      pageSize: 'pageSize',
      postAllRepliesContent: 'postAllRepliesContent',
      postsTags: 'postsTagsMap',
      postsReplyNumber: 'postsReplyNumberMap',
      filteredThreads: 'filteredThreads',
      likesForPage: 'likesForPage',
      sortRules: 'sortRules',
      sortRuleIndex: 'sortRuleIndex'
    }),

    ...mapGetters('forum', {
      posts: 'postsArr',
      threads: 'threadsArr'
    }),

    searchText () {
      return this.$route.query.searchText || ''
    },

    currentPage () {
      return Number.parseInt(this.$route.query.page) || 1
    },

    threadsForPage () {
      const startIndex = (this.currentPage - 1) * this.pageSize
      this.pageViewSwitch = !this.pageViewSwitch
      return this.filteredThreads.slice(startIndex, startIndex + this.pageSize)
    },

    listCssStyle () {
      const colWidth = 64
      const colMargin = 8
      const lastReplyWidth = 120
      const totalColumns = 2
      const sortWidth = 76
      return {
        '--col-width': `${colWidth}px`,
        '--col-margin': `${colMargin}px`,
        '--last-reply-width': `${lastReplyWidth}px`,
        '--sort-width': `${sortWidth}px`,
        '--title-width': `calc(100% - ${(totalColumns * (colWidth + colMargin)) + lastReplyWidth + sortWidth}px)`
      }
    }
  },

  watch: {
    searchText () {
      this.setFilter()
    },

    // triggers when switching
    '$route.query.tags' () {
      this.setFilter()
    },

    threadsForPage () {
      this.updateLikesForPage()
    },

    userId (val) {
      if (val) {
        this.setThreads()
      }
    }
  },

  created () {
    if (this.userId) {
      this.setThreads()
    }
  },

  mounted () {
    this.$log('forum_view', 'home')
  },

  methods: {
    removeTag (tag) {
      const query = { ...this.$route.query }
      query.tags = query.tags.split(',').filter(existingTag => existingTag !== tag)

      if (query.tags.length === 0) {
        delete query.tags
      } else {
        query.tags = query.tags.join(',')
      }
      this.$router.replace({ query })
      this.setFilter()
    },

    /**
     * Set up threads for the forum homepage
     */
    async setThreads () {
      this.loading = true
      await this.fetchAllUsers()

      // if filtering, fetch all
      if (this.$route.query.tags || this.$route.query.searchText || this.currentPage !== 1) {
        await this.$store.dispatch('forum/fetchPosts')
        this.$store.dispatch('forum/getThreads', { users: this.users })
        await this.setFilter()
        this.loading = false
      } else {
        await this.$store.dispatch('forum/fetchPostsFirstPage') // todo consider filter on server
        this.$store.dispatch('forum/getThreads', { users: this.users })
        const sortRuleIndexUrl = this.$route.query.sortRule
        if (sortRuleIndexUrl) {
          this.$store.commit('forum/updateSortRuleIndex', Number(sortRuleIndexUrl))
        }
        await this.setFilter()
        this.loading = false

        await this.$store.dispatch('forum/fetchPosts')
        this.$store.dispatch('forum/getThreads', { users: this.users })
        await this.setFilter()
      }
    },

    /**
     * Filter threads given filter text
     */
    async setFilter () {
      await this.$store.dispatch('forum/filterThreads', this.tags)
    },

    /**
     * Event handler for when the page changes
     * @param {String} pageNumber - The new page number
     */
    onPageChange (pageNumber) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pageNumber
        }
      })
    },

    /**
     * Route to post detail page after click
     * @param {Object} thread
     */
    openPostDetail (thread) {
      this.$router.push({
        name: 'Forum Post',
        params: {
          id: thread.id,
          postData: thread
        }
      })
    },

    /**
     * Fetch total likes for posts on page
     */
    async updateLikesForPage () {
      await this.$store.dispatch('forum/fetchLikesForPage', this.threadsForPage.map(item => item.id))
    },

    /**
     * Update the the ordering of the list
     * given the chosen sort rule
     */
    onUpdateSort (value) {
      this.$store.commit('forum/updateSortRuleIndex', this.sortRules[value])
      this.$store.dispatch('forum/sortThreads', this.sortRuleIndex)
      const query = _.cloneDeep(this.$route.query)
      query.sortRule = this.sortRuleIndex
      this.$router.replace({ query })
    }
  }
}
</script>

<style scoped>
.forum-content {
  margin: 0 auto;
  padding: 32px 8px;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.thread-list-wrapper {
  margin: 0;
  padding: 8px 0;
  width: 100%;
  height: 100%;
  position: relative;
  min-height: 150px;
  display: flex;
  justify-content: center;
  background-color: white;
  border-radius: 10px;
}

.thread-list-wrapper >>> .ivu-spin-fix {
  color: var(--color-green-thick);
  background-color: transparent;
}

.thread-list-wrapper >>> .ivu-spin-fix p {
  margin-top: 8px;
  font-weight: bold;
}

.thread-list {
  width: 100%;
}

.thread-list >>> .ivu-list-header {
  padding: 0;
}

.thread-list-header {
  padding: 8px 16px;
  display: flex;
  flex-direction: row;
  font-size: 14px;
  font-weight: bold;
  color: var(--color-text-light);
}

.thread-heading {
  display: flex;
  flex-direction: row;
  align-items: top;
  width: var(--title-width);
}

.thread-sticky,
.thread-reply,
.thread-like {
  margin-right: var(--col-margin);
  width: var(--col-width);
  text-align: center;
}

.thread-last-updated {
  width: var(--last-reply-width);
}

.thread-sort {
  width: var(--sort-width);
}

.thread-sort a {
  color: var(--color-text-light);
}

@media screen and (min-width: 1024px) {
  .forum-content {
    max-width: 1024px;
  }
}

@media screen and (min-width: 1440px) {
  .forum-content {
    padding: 32px 64px;
    max-width: 1440px;
  }
}

.tag-list {
  margin-bottom: 16px;
}

.post-tag {
  background-color: transparent;
  cursor: pointer;
}

.post-tag >>> .ivu-tag-text {
  color: var(--color-green-thick);
}
</style>
